import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, InfoIcon } from 'lucide-react';
import { planNotes, plans } from '../data/pricing';
export function PricingPlan() {
  return <>
      <section className="relative overflow-hidden bg-ink pb-14 pt-14 lg:pb-16 lg:pt-20" data-id="element-532" data-mp="1m2vph2k5l37c">
        <div className="absolute inset-0 bp opacity-80" aria-hidden="true" data-id="element-533" data-mp="1r04adaworlca" />
        <div className="absolute -left-32 top-0 h-[32rem] w-[32rem] rounded-full bg-forge-orange/[0.09] blur-[130px]" aria-hidden="true" data-id="element-534" data-mp="2eqrjfzic8h0f" />

        <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-535" data-mp="2jqdx2a22vz9r">
          <p className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.24em] text-forge-cyan" data-id="element-536" data-mp="24z3v47h2vg36">
            <span className="h-px w-7 bg-forge-orange" aria-hidden="true" data-id="element-537" data-mp="29wneflsmuj1z" />
            Pricing plan
          </p>
          <h1 className="mt-4 max-w-3xl font-display text-[2rem] font-extrabold leading-[1.08] tracking-[-0.02em] text-white sm:text-[2.7rem]" data-id="element-538" data-mp="32f0z0joyk8j4">
            Scoped to the build, priced before we start.
          </h1>
          <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-mist-200" data-id="element-539" data-mp="17lke12vuk0xm">
            We do not price a website before we understand the business behind it. These are the shapes our work usually takes
            — the number comes after the conversation, in writing.
          </p>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy-900 py-14 lg:py-16" data-id="element-540" data-mp="1v9lgpv6cgn54">
        <div className="absolute inset-0 bp opacity-50" aria-hidden="true" data-id="element-541" data-mp="1qc1r5z474nxv" />
        <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-542" data-mp="258stag598dr6">
          <div className="grid gap-5 lg:grid-cols-3" data-id="element-543" data-mp="258169tpq3oci">
            {plans.map((plan, i) => <motion.article key={plan.name} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.42,
            delay: i * 0.07
          }} className={`relative flex flex-col overflow-hidden rounded-2xl border p-7 ${plan.featured ? 'border-forge-orange/40 bg-navy-950' : 'border-white/10 bg-navy-950/70'}`} data-id="element-544" data-mp="20ahia2383x7d" data-mpi={i}>
                {plan.featured && <>
                    <span className="absolute inset-x-0 top-0 h-[3px] bg-forge-orange" aria-hidden="true" data-id="element-545" data-mp="14mv9v5ozslyz" />
                    <span className="absolute right-5 top-5 rounded-full bg-forge-orange/15 px-3 py-1 font-mono text-[9.5px] uppercase tracking-[0.16em] text-forge-orange" data-id="element-546" data-mp="1eiolmmuw0bfp">
                      Most requested
                    </span>
                  </>}
                <h2 className="font-display text-[1.35rem] font-extrabold text-white" data-id="element-547" data-mp="1nbs3d7ckfag5">{plan.name}</h2>
                <p className="mt-2 text-[12.5px] uppercase tracking-[0.12em] text-forge-cyan" data-id="element-548" data-mp="1sc7hi14o8lz6">{plan.priceNote}</p>
                <p className="mt-4 text-[13.5px] leading-relaxed text-mist-200" data-id="element-549" data-mp="1x7d2mxq91wm7">{plan.summary}</p>
                <p className="mt-4 rounded-xl bg-white/[0.04] px-4 py-3 text-[12.5px] leading-relaxed text-mist-300" data-id="element-550" data-mp="225wo6x0xl6e1">
                  <span className="font-semibold text-white" data-id="element-551" data-mp="3hri4rz3gh4pc">Best for: </span>
                  {plan.bestFor}
                </p>

                <ul className="mt-6 space-y-2.5 border-t border-white/10 pt-6" data-id="element-552" data-mp="3ct6vpqkziy86">
                  {plan.includes.map(inc => <li key={inc} className="flex gap-2.5 text-[13.5px] leading-snug text-mist-100" data-id="element-553" data-mp="37uvprqxt97v8">
                      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-forge-green/15" data-id="element-554" data-mp="9dhxn7b4grjz">
                        <CheckIcon className="h-3 w-3 text-forge-green" aria-hidden="true" data-id="element-555" data-mp="3vqzz0tzita63" />
                      </span>
                      {inc}
                    </li>)}
                </ul>

                <Link to="/#get-started" className={`group mt-8 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-[14.5px] font-bold transition-transform hover:-translate-y-0.5 ${plan.featured ? 'bg-forge-orange text-ink' : 'border border-white/15 text-white hover:bg-white/5'}`} data-id="element-556" data-mp="s1n08bg1m6h7">
                  Get a scope and price
                  <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-557" data-mp="3u1ok5w2p7xki" />
                </Link>
              </motion.article>)}
          </div>

          <ul className="mt-8 grid gap-3 lg:grid-cols-3" data-id="element-558" data-mp="qk6f1web94ns">
            {planNotes.map(note => <li key={note} className="flex gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4" data-id="element-559" data-mp="vi802mvare1w">
                <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-forge-cyan" aria-hidden="true" data-id="element-560" data-mp="grscj1feru0k" />
                <p className="text-[13px] leading-relaxed text-mist-200" data-id="element-561" data-mp="lqv6nqtuoftr">{note}</p>
              </li>)}
          </ul>
        </div>
      </section>
    </>;
}